/**
 * Formats a numeric value by adding commas to separate thousands, millions, etc.
 *
 * @param value - The numeric value to be formatted. It can be either a number or a string.
 * @param decimals - The number of decimal places to display.
 * @returns The formatted numeric value as a string with commas or the original value if not a valid number.
 */
export const withCommas = (value: number | string, decimals?: number): string | number => {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numericValue)) {
    return value;
  }

  if (decimals !== undefined) {
    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  // Preserve existing decimals
  const strValue = String(value);
  const decimalPlaces = strValue.includes(".") ? strValue.split(".")[1].length : 0;

  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

/**
 * Format numbers into dollar strings with suffixes.
 *
 * Examples:
 *  1_500_000_000 → "$1.5B"
 *  12_000_000    → "$12M"
 *  450_000       → "$450k"
 */
export const formatCurrencyCompact = (value: number): string => {
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const suffixes = [
    { limit: 1_000_000_000, divisor: 1_000_000_000, suffix: "B", decimals: 2 },
    { limit: 1_000_000, divisor: 1_000_000, suffix: "M", decimals: 2 },
    { limit: 1_000, divisor: 1_000, suffix: "k", decimals: 1 },
  ];

  for (const { limit, divisor, suffix, decimals } of suffixes) {
    if (absValue >= limit) {
      const formatted = (absValue / divisor).toFixed(decimals).replace(/\.0$/, "");
      return `${isNegative ? "-$" : "$"}${formatted}${suffix}`;
    }
  }

  return `${isNegative ? "-$" : "$"}${withCommas(absValue.toFixed(0))}`;
};

/**
 * Format numbers into dollar strings.
 *
 * @param value - The numeric value to be formatted.
 * @param compact - If true, uses compact format with suffixes (e.g., "$1.5B", "$12M", "$450k").
 *                  If false, uses full number format with commas (e.g., "$1,500,000,000").
 * @returns The formatted currency string.
 */
export const formatCurrency = (value: number, compact: boolean = true): string => {
  if (compact) return formatCurrencyCompact(value);
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  return `${isNegative ? "-$" : "$"}${withCommas(absValue, 2)}`;
};

export const getAbsoluteValue = (value: number): number => {
  return value < 0 ? -1 * value : value;
};

export const numCompare = (get: (r: any) => number | null) => (a: any, b: any) => {
  // If the value is null, we want to sort it to the bottom
  const va = get(a) ?? Number.MIN_SAFE_INTEGER;
  const vb = get(b) ?? Number.MIN_SAFE_INTEGER;
  return va - vb;
};

export const formatPercent = (value: number | null | undefined, decimals: number = 4) => {
  return value === null || value === undefined ? "-" : `${(value * 100).toFixed(decimals)}%`;
};

const abbreviate = (value: number, decimals: { thousands: number; default: number }) => {
  const millions = Number((value / 1_000_000).toFixed(2));
  const thousands = Number((value / 1000).toFixed(decimals.thousands));
  const units = Number(value.toFixed(decimals.default));

  if (millions >= 1000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (thousands >= 1000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (units >= 100_000) return `${(value / 1000).toFixed(decimals.thousands)}k`;

  return withCommas(value.toFixed(decimals.default));
};

export const formatAbbreviatedValue = (value: string | number, unit?: string | null) => {
  if (typeof value !== "number") return value;

  return abbreviate(value, { thousands: 1, default: unit === "%" ? 4 : 2 });
};

export const formatAbbreviatedValueWithUnit = (value: number, unit: "%" | "$" | null): string => {
  const isNegative = value < 0;
  const absolute = Math.abs(value);

  if (unit === "$") {
    return `${isNegative ? "-$" : "$"}${abbreviate(absolute, { thousands: 2, default: 0 })}`;
  }

  return `${isNegative ? "-" : ""}${withCommas(absolute.toFixed(2))}${unit ?? ""}`;
};

/**
 * Half a display ulp: below this, a value rounds to zero at the precision we print
 * (4dp for percent and percentage points, 2dp for currency).
 */
export const displayEpsilon = (unit: "%" | "$"): number => (unit === "$" ? 5e-3 : 5e-5);

/**
 * Anything that renders as zero is treated as an actual zero, so `-0.0000` can't reach the screen.
 * Sign is only ever shown on a magnitude the reader can actually see.
 */
export const normalizeDisplayZero = (value: number, unit: "%" | "$"): number =>
  Math.abs(value) < displayEpsilon(unit) ? 0 : value;

export const formatAbbreviatedValueWithMetricUnit = (value: number, unit: "%" | "$") => {
  const shown = normalizeDisplayZero(value, unit);
  return unit === "%"
    ? `${shown.toFixed(4)}${unit}`
    : `${shown < 0 ? "-" : ""}${unit}${withCommas(getAbsoluteValue(shown).toFixed(2))}`;
};
